import React from 'react';

import EggFried from '@pohodnik/icons/svg/RealWorld/EggFried';
import clsx from 'clsx';

import { stringToColour } from '@helpers/colorsizeStringHelper';

import avaStyle from './style.module.less';
import nophoto from '../../img/nophoto.png';
import PohLoader from '../Loader';

const getShortAlt = (str: string): string => {
  if (!str.length) {
    return '';
  }

  if (str.length < 3) {
    return str.toUpperCase();
  }

  if (/\s/.test(str)) {
    const words = str.split(/\s/).filter((w) => w.length > 2 && /^[А-яA-z-]+$/.test(w));
    return words
      .slice(0, 2)
      .map((x) => x[0].toUpperCase())
      .join('');
  }

  return str[0].toUpperCase();
};

function normalizeSrc(src: string): string {
  if (!src) {
    return src;
  }
  const host = window.location.host.indexOf('localhost') === -1 ? window.location.origin : '';
  return src.indexOf('http') !== 0 && /^[A-z]/.test(src) ? `${host}/${src}` : src;
}

export interface IPohAvaProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string;
  alt?: string;
  icon?: React.ReactElement<any>;
  width?: string | number;
  iconName?: string;
  color?: string;
  colorize?: boolean;
}

const PohAva: React.FC<IPohAvaProps> = ({
  src: rawSrc,
  icon: IconComp,
  alt,
  style,
  width,
  className,
  iconName,
  color,
  colorize = false,
  ...rest
}) => {
  const src = rawSrc ? normalizeSrc(rawSrc) : undefined;
  const [loaded, setLoaded] = React.useState(!src);
  const [error, setError] = React.useState(false);

  let IconComponent: React.FC | null = null;

  if (IconComp) {
    IconComponent = () => React.cloneElement(IconComp, { className: avaStyle.avaSvg });
  }

  if (iconName) {
    // eslint-disable-next-line react/display-name
    IconComponent = () => <EggFried />;
  }

  React.useEffect(() => {
    if (!src) {
      return;
    }
    if (src) {
      const img = document.createElement('img');
      img.src = normalizeSrc(src);
      img.onload = () => setLoaded(true);
      img.onerror = () => {
        setError(true);
        setLoaded(true);
      };
    } else {
      setLoaded(true);
    }
  }, [src]);

  const oStyle = {
    width: '32px',
    height: '32px',
    minWidth: '32px',
    color: 'rgba(255,255,255,0.7)',
    ...style,
  };

  if (width) {
    const w: string | number = width;
    Object.assign(oStyle, { width: w, minWidth: w, height: w });
  }

  if (src) {
    Object.assign(oStyle, { backgroundImage: `url(${src})` });
  }

  if (error && !IconComp && !alt) {
    Object.assign(oStyle, { backgroundImage: `url(${nophoto})` });
  }

  const needFallback = !src || error;

  if (needFallback && alt) {
    Object.assign(oStyle, {
      backgroundColor: colorize
        ? (color ?? stringToColour(alt))
        : (oStyle.backgroundColor ?? '#f1f1f1'),
    });
  }

  return (
    <div style={oStyle} className={clsx(avaStyle.ava, className)} {...rest}>
      {!loaded && <PohLoader />}
      {loaded && needFallback && IconComponent && <IconComponent />}
      {loaded && needFallback && !IconComponent && alt && getShortAlt(alt)}
    </div>
  );
};

export default PohAva;
